/**
 * @voltxampere/math — Sparse matrix in Compressed Sparse Row format
 *
 * SPECS §3.10 specifies CSR with three arrays: `values` and
 * `colIndices` of length `nnz`, plus `rowPointers` of length
 * `rows + 1`. Row `i`'s nonzeros sit at indices
 * `[rowPointers[i], rowPointers[i+1])` in both `values` and
 * `colIndices`.
 *
 * The construction path here takes COO triplets — the format users
 * actually carry around (one per stamp in MNA) — and consolidates
 * them into a CSR matrix. Duplicate `(row, col)` triplets are summed
 * (essential for stamping: a node admittance often gets contributions
 * from multiple components).
 *
 * Production-grade sparse LU lives in Sprint 10+; Sprint 4 ships
 * `denseToSparse`/`sparseToDense` so the dense LU path can be wrapped
 * into a sparse-input solver in `lu/sparse-lu.ts`.
 *
 * Indexed access uses `?? 0` to satisfy `noUncheckedIndexedAccess`
 * without resorting to non-null assertions (banned by the project
 * lint config).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { createMatrix, matrixSet } from "../dense/matrix.js";
function assertDim(value, name, op) {
    if (!Number.isInteger(value) || value < 0) {
        throw new ContractViolationError(`${op}: ${name} must be a non-negative integer, got ${String(value)}`);
    }
}
/**
 * Build a CSR matrix from `triplets`. Duplicates `(row, col)` are
 * summed; explicit zeros (after summation) are dropped from storage.
 * Triplets must reference indices within `[0, rows) × [0, cols)`.
 */
export function createSparse(rows, cols, triplets) {
    assertDim(rows, "rows", "createSparse");
    assertDim(cols, "cols", "createSparse");
    // Bucket triplets by row, summing duplicates per (row, col).
    // We use a Map<col, value> per row to keep accumulation O(nnz · log nnz)
    // amortised without sorting the entire triplet stream.
    const perRow = Array.from({ length: rows }, () => new Map());
    for (const [k, t] of triplets.entries()) {
        if (!Number.isInteger(t.row) || t.row < 0 || t.row >= rows) {
            throw new ContractViolationError(`createSparse: triplet[${String(k)}].row=${String(t.row)} out of range [0, ${String(rows)})`);
        }
        if (!Number.isInteger(t.col) || t.col < 0 || t.col >= cols) {
            throw new ContractViolationError(`createSparse: triplet[${String(k)}].col=${String(t.col)} out of range [0, ${String(cols)})`);
        }
        if (!Number.isFinite(t.value)) {
            throw new ContractViolationError(`createSparse: triplet[${String(k)}].value must be finite, got ${String(t.value)}`);
        }
        const bucket = perRow[t.row];
        if (bucket === undefined) {
            // Unreachable — `perRow` was sized `rows` and `t.row < rows` is
            // checked above. Defensive guard for the type system.
            throw new ContractViolationError(`createSparse: bucket missing for row ${String(t.row)}`);
        }
        bucket.set(t.col, (bucket.get(t.col) ?? 0) + t.value);
    }
    // Count nonzeros (after sum, dropping exact zeros).
    let nnz = 0;
    for (const bucket of perRow) {
        for (const v of bucket.values()) {
            if (v !== 0)
                nnz++;
        }
    }
    const values = new Float64Array(nnz);
    const colIndices = new Uint32Array(nnz);
    const rowPointers = new Uint32Array(rows + 1);
    let cursor = 0;
    for (const [i, bucket] of perRow.entries()) {
        rowPointers[i] = cursor;
        // Sort columns within the row — CSR allows arbitrary order, but
        // sorted columns give predictable iteration and faster `sparseGet`.
        const colsSorted = Array.from(bucket.keys()).sort((a, b) => a - b);
        for (const col of colsSorted) {
            const v = bucket.get(col) ?? 0;
            if (v === 0)
                continue;
            values[cursor] = v;
            colIndices[cursor] = col;
            cursor++;
        }
    }
    rowPointers[rows] = cursor;
    return { rows, cols, values, colIndices, rowPointers };
}
/** Read element `(i, j)`. Returns 0 if not stored. */
export function sparseGet(m, i, j) {
    if (!Number.isInteger(i) || i < 0 || i >= m.rows) {
        throw new ContractViolationError(`sparseGet: row index ${String(i)} out of range`);
    }
    if (!Number.isInteger(j) || j < 0 || j >= m.cols) {
        throw new ContractViolationError(`sparseGet: column index ${String(j)} out of range`);
    }
    const start = m.rowPointers[i] ?? 0;
    const end = m.rowPointers[i + 1] ?? 0;
    // Binary search — columns within a row are sorted ascending after `createSparse`.
    let lo = start;
    let hi = end - 1;
    while (lo <= hi) {
        const mid = (lo + hi) >>> 1;
        const c = m.colIndices[mid] ?? 0;
        if (c === j)
            return m.values[mid] ?? 0;
        if (c < j)
            lo = mid + 1;
        else
            hi = mid - 1;
    }
    return 0;
}
/** Sparse matrix–vector product. */
export function sparseVectorMul(m, v) {
    if (v.length !== m.cols) {
        throw new ContractViolationError(`sparseVectorMul: vector length ${String(v.length)} ≠ cols ${String(m.cols)}`);
    }
    const out = new Float64Array(m.rows);
    for (let i = 0; i < m.rows; i++) {
        const start = m.rowPointers[i] ?? 0;
        const end = m.rowPointers[i + 1] ?? 0;
        let acc = 0;
        for (let k = start; k < end; k++) {
            const colIdx = m.colIndices[k] ?? 0;
            acc += (m.values[k] ?? 0) * (v[colIdx] ?? 0);
        }
        out[i] = acc;
    }
    return out;
}
/** Number of stored nonzero entries. */
export function sparseNNZ(m) {
    return m.values.length;
}
/** Convert to a fresh dense matrix. */
export function sparseToDense(m) {
    const dense = createMatrix(m.rows, m.cols);
    for (let i = 0; i < m.rows; i++) {
        const start = m.rowPointers[i] ?? 0;
        const end = m.rowPointers[i + 1] ?? 0;
        for (let k = start; k < end; k++) {
            matrixSet(dense, i, m.colIndices[k] ?? 0, m.values[k] ?? 0);
        }
    }
    return dense;
}
/**
 * Convert dense to sparse, dropping entries with `|v| <= drop`. The
 * default of 0 keeps every nonzero (deterministic structure preserved).
 */
export function denseToSparse(m, drop = 0) {
    if (!Number.isFinite(drop) || drop < 0) {
        throw new ContractViolationError(`denseToSparse: drop must be a non-negative finite number, got ${String(drop)}`);
    }
    const triplets = [];
    for (let i = 0; i < m.rows; i++) {
        for (let j = 0; j < m.cols; j++) {
            const v = m.data[i * m.cols + j] ?? 0;
            if (Math.abs(v) > drop)
                triplets.push({ row: i, col: j, value: v });
        }
    }
    return createSparse(m.rows, m.cols, triplets);
}
//# sourceMappingURL=csr.js.map